import { Button, Card, ToggleButton, ToggleButtonGroup } from '@mui/material';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
	type PaymentSummaryParameters,
	type PaymentSummaryRateType,
	type PaymentSummaryReportFormat,
	paymentSummaryRateTypes,
} from '@parkchirp/reporting';
import type { Dayjs } from 'dayjs';
import { useMemo, useState } from 'react';
import { AccountFilter, type AccountFilterValue } from './AccountFilter';
import { DateRangeFilter, type DateRangeValue } from './DateRangeFilter';
import { FacilityHierarchyFilter, type FacilityHierarchyFilterValue } from './FacilityHierarchyFilter';
import { applyFacilityHierarchyToParameters } from './facilityHierarchyParameters';
import { RateTypesFilter } from './RateTypesFilter';
import { paymentReportsInitialDateRange } from './shared';
import { reportingPanelStyles } from './styles';
import type { ReportFormProps } from './types';

const initialFacilityHierarchy: FacilityHierarchyFilterValue = {
	areas: [],
	branches: [],
	districts: [],
	organizations: [],
	facilities: [],
};

const defaultRateTypes: PaymentSummaryRateType[] = [...paymentSummaryRateTypes];

const addDate = (
	parameters: PaymentSummaryParameters,
	key: 'transactionStartDate' | 'transactionEndDate' | 'applicationStartDate' | 'applicationEndDate',
	date: Dayjs | null,
) => {
	if (date) {
		parameters[key] = date.format('YYYY-MM-DD');
	}
};

export const PaymentSummaryReportForm = ({
	createReport,
	isGeneratingReport,
	organizationId,
	isSuperAdmin,
	onValidationError,
}: ReportFormProps) => {
	const [dateRange, setDateRange] = useState<DateRangeValue>(paymentReportsInitialDateRange);
	const [accountFilter, setAccountFilter] = useState<AccountFilterValue>({ type: 'all' });
	const [rateTypes, setRateTypes] = useState<PaymentSummaryRateType[]>(defaultRateTypes);
	const [facilityHierarchy, setFacilityHierarchy] = useState<FacilityHierarchyFilterValue>(initialFacilityHierarchy);
	const [format, setFormat] = useState<PaymentSummaryReportFormat>('csv');

	const canSubmit = useMemo(() => {
		if (rateTypes.length === 0) {
			return false;
		}

		if (dateRange.dateBasis === 'transactionDate') {
			return Boolean(dateRange.transactionStartDate);
		}

		return Boolean(dateRange.applicationStartDate);
	}, [dateRange, rateTypes]);

	const reset = () => {
		setDateRange(paymentReportsInitialDateRange);
		setAccountFilter({ type: 'all' });
		setRateTypes(defaultRateTypes);
		setFacilityHierarchy(initialFacilityHierarchy);
		setFormat('csv');
	};

	const buildAccountFilter = (): PaymentSummaryParameters['accountFilter'] => {
		switch (accountFilter.type) {
			case 'accountId':
				return {
					type: 'accountId',
					value: Number(accountFilter.value),
				};

			case 'subscriptionName':
				return {
					type: 'subscriptionName',
					value: accountFilter.value.trim(),
				};
			default:
				return undefined;
		}
	};

	const buildParameters = (): PaymentSummaryParameters => {
		const accountFilter = buildAccountFilter();
		let parameters: PaymentSummaryParameters;

		if (dateRange.dateBasis === 'transactionDate') {
			if (!dateRange.transactionStartDate) {
				throw new Error('Select a transaction start date.');
			}

			parameters = {
				rateTypes,
				dateOrder: 'transactionDate',
				transactionStartDate: dateRange.transactionStartDate.format('YYYY-MM-DD'),
				format,
			};

			addDate(parameters, 'transactionEndDate', dateRange.transactionEndDate);
			addDate(parameters, 'applicationStartDate', dateRange.applicationStartDate);
			addDate(parameters, 'applicationEndDate', dateRange.applicationEndDate);
		} else {
			if (!dateRange.applicationStartDate) {
				throw new Error('Select an application start date.');
			}

			parameters = {
				rateTypes,
				dateOrder: 'applicationDate',
				applicationStartDate: dateRange.applicationStartDate.format('YYYY-MM-DD'),
				format,
			};

			addDate(parameters, 'applicationEndDate', dateRange.applicationEndDate);
			addDate(parameters, 'transactionStartDate', dateRange.transactionStartDate);
			addDate(parameters, 'transactionEndDate', dateRange.transactionEndDate);
		}

		if (accountFilter) {
			parameters.accountFilter = accountFilter;
		}

		applyFacilityHierarchyToParameters(parameters, facilityHierarchy);

		if (facilityHierarchy.facilities.length === 1) {
			const facilityNumber = facilityHierarchy.facilities[0].facilityNumber;
			if (facilityNumber !== undefined && facilityNumber !== null && `${facilityNumber}` !== '') {
				parameters.facilityNumber = facilityNumber;
			}
		}

		return parameters;
	};

	const validate = () => {
		if (rateTypes.length === 0) {
			onValidationError('Select at least one rate type.');
			return false;
		}

		if (dateRange.dateBasis === 'transactionDate' && !dateRange.transactionStartDate) {
			onValidationError('Select a transaction start date.');
			return false;
		}

		if (dateRange.dateBasis === 'applicationDate' && !dateRange.applicationStartDate) {
			onValidationError('Select an application start date.');
			return false;
		}

		if (accountFilter.type === 'accountId' && !accountFilter.value) {
			onValidationError('Enter an account ID.');
			return false;
		}

		if (accountFilter.type === 'subscriptionName' && !accountFilter.value.trim()) {
			onValidationError('Enter a subscription name.');
			return false;
		}

		return true;
	};

	const submit = async () => {
		if (!validate()) {
			return;
		}

		await createReport({
			reportType: 'paymentSummary',
			parameters: buildParameters(),
		});
	};

	return (
		<Card
			variant='outlined'
			sx={(theme) => ({
				...reportingPanelStyles(theme),
				overflow: 'hidden',
				p: 3,
			})}
		>
			<Stack spacing={2.5}>
				<DateRangeFilter value={dateRange} onChange={setDateRange} />

				<Divider />

				<AccountFilter filter={accountFilter} onChange={setAccountFilter} />

				<Divider />

				<RateTypesFilter
					selectedRateTypes={rateTypes}
					onChange={(value) => setRateTypes(value as PaymentSummaryRateType[])}
				/>

				<Divider />

				<FacilityHierarchyFilter
					organizationId={organizationId}
					showOrganizations={isSuperAdmin}
					value={facilityHierarchy}
					onChange={setFacilityHierarchy}
				/>

				<Divider />

				<Stack spacing={1.5}>
					<Typography variant='subtitle1' fontWeight={600}>
						Output
					</Typography>

					<ToggleButtonGroup
						exclusive
						size='small'
						value={format}
						onChange={(_, nextFormat: PaymentSummaryReportFormat | null) => {
							if (nextFormat) {
								setFormat(nextFormat);
							}
						}}
					>
						<ToggleButton value='csv'>CSV</ToggleButton>
						<ToggleButton value='pdf'>PDF</ToggleButton>
						<ToggleButton value='xlsx'>Excel</ToggleButton>
					</ToggleButtonGroup>
				</Stack>

				<Divider />

				<Stack
					direction='row'
					justifyContent='flex-end'
					spacing={2}
					sx={{
						borderColor: 'divider',
						borderTop: 1,
						pt: 2,
					}}
				>
					<Button onClick={reset} disabled={isGeneratingReport}>
						Reset
					</Button>

					<Button onClick={submit} disabled={!canSubmit || isGeneratingReport}>
						Generate Report
					</Button>
				</Stack>
			</Stack>
		</Card>
	);
};
